import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Container, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  getErrorContext?: () => Record<string, unknown>;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

function ErrorFallback({
  error,
  errorInfo,
  onReset,
}: {
  error?: Error;
  errorInfo?: ErrorInfo;
  onReset: () => void;
}) {
  const { t } = useTranslation();

  const handleReportError = () => {
    if (error) {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo?.componentStack,
          },
        },
      });
      notifications.show({
        title: t('error.reportError', 'Report Error'),
        message: t('error.reportErrorMessage', 'Error reported'),
        color: 'green',
      });
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Alert title={t('errorBoundary.title', 'Something went wrong')} color="red" variant="light">
          <Text size="sm" mb="md">
            {t(
              'errorBoundary.message',
              'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'
            )}
          </Text>

          {(import.meta.env.DEV ?? false) && error && (
            <details style={{ marginTop: '1rem' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                {t('errorBoundary.errorDetails', 'Error Details')}
              </summary>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '1rem',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '12px',
                  marginTop: '0.5rem',
                }}
              >
                {error.toString()}
                {errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </Alert>

        <Stack gap="sm">
          <Button onClick={onReset} variant="filled" color="blue">
            {t('errorBoundary.tryAgain', 'Try Again')}
          </Button>

          <Button onClick={handleReportError} variant="outline" color="red">
            {t('errorBoundary.reportError', 'Report Error')}
          </Button>

          <Button onClick={handleReload} variant="subtle">
            {t('errorBoundary.refreshPage', 'Refresh Page')}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    // Log error to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      extra: this.props.getErrorContext?.(),
    });

    // Log to console in development
    if (import.meta.env.DEV ?? false) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
